import type { CityConfig } from "@/pages/city-template/types";

export const hydeParkConfig: CityConfig = {
  city: "Hyde Park",
  state: "New York",
  stateAbbr: "NY",
  slug: "hyde-park",
  route: "/cities/hyde-park",
  title: "Emergency & Expedited Passport Hyde Park NY | Same Day Passport Hyde Park",
  metaDescription:
    "Emergency passport Hyde Park NY — same day, 24-hour, and 3-5 day expedited passport processing for Dutchess County. Fast Passport Center serves Hyde Park, FDR country, Rhinebeck, and all Hudson Valley communities.",
  h1: "Emergency & Expedited Passport Services in Hyde Park, NY",
  heroSubtitle:
    "Hyde Park's fast passport solution — from the historic FDR estate to the Hudson Valley. Whether you're flying out of Stewart International or Albany, Fast Passport Center delivers emergency passports in as little as 24 hours and expedited processing in 3-5 days.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Historic%20FDR%20estate%20grounds%20in%20Hyde%20Park%20New%20York%20along%20the%20Hudson%20River%2C%20beautiful%20manicured%20gardens%20with%20river%20valley%20views%2C%20Dutchess%20County%20countryside%20with%20rolling%20hills%2C%20warm%20autumn%20afternoon%20light%2C%20modern%20travel%20photography%20with%20warm%20tones%2C%20no%20text%20overlay%2C%20scenic%20Hudson%20Valley%20estate%20landscape%20with%20river%20and%20hills%20in%20background&width=1600&height=700&seq=ny-hyde-park-hero-001&orientation=landscape",
  heroImageAlt: "Historic FDR estate grounds in Hyde Park along the Hudson River with Dutchess County countryside",
  geoPosition: "41.7948; -73.9338",
  geoPlacename: "Hyde Park, New York, United States",
  lastModified: "2026-05-31",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~85 miles (South in Manhattan)",
  },
  nearbyCitiesZips: [
    { name: "Hyde Park", zips: "12538" },
    { name: "Rhinebeck", zips: "12572" },
    { name: "Poughkeepsie", zips: "12601, 12602, 12603" },
    { name: "Wappingers Falls", zips: "12590" },
    { name: "Staatsburg", zips: "12580" },
    { name: "Red Hook", zips: "12571" },
    { name: "Tivoli", zips: "12583" },
    { name: "Millbrook", zips: "12545" },
    { name: "Amenia", zips: "12501" },
    { name: "Pleasant Valley", zips: "12569" },
    { name: "Clinton Corners", zips: "12514" },
    { name: "Rhinecliff", zips: "12574" },
    { name: "Kingston", zips: "12401, 12402" },
    { name: "Newburgh", zips: "12550" },
    { name: "Beacon", zips: "12508" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency passport processing for Hyde Park travelers with urgent departures — same day service available for documented emergencies.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with direct access to the New York Passport Agency.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround. Available for Hyde Park residents 7 days a week.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Southern Dutchess County",
      desc: "Hyde Park, Staatsburg, Rhinebeck, and the entire Hudson Valley — we serve all of southern Dutchess County.",
    },
  ],
  testimonials: [
    {
      name: "William R.",
      location: "Hyde Park, NY",
      rating: 5,
      text: "Had a family emergency in Ireland and needed my passport in 3 days. Fast Passport Center delivered in 4 days with constant updates. Truly a lifesaver during a stressful time.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia M.",
      location: "Staatsburg, NY",
      rating: 5,
      text: "Renewed my passport for a cruise to the Caribbean. Fast Passport Center made it simple — the 5-day option was perfect. Had it well before our departure from Stewart Airport.",
      service: "Passport Renewal",
    },
    {
      name: "James K.",
      location: "Poughkeepsie, NY",
      rating: 5,
      text: "First-time passport for my son's graduation trip to Europe. The agent was incredibly helpful with the DS-11 form and acceptance clerk appointment. Processed in 5 days.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "hyde-park-1",
      name: "Hyde Park Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "424 Albany Post Road",
      city: "Hyde Park",
      state: "NY",
      zip: "12538",
      phone: "845-229-8074",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Hyde Park",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main Hyde Park passport acceptance facility. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "hyde-park-2",
      name: "Poughkeepsie Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "55 Mansion Street",
      city: "Poughkeepsie",
      state: "NY",
      zip: "12601",
      phone: "845-452-4050",
      hours: "Mon-Fri 8:00 AM - 5:00 PM, Sat 9:00 AM - 1:00 PM",
      distance: "Poughkeepsie (~8 miles south)",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major Poughkeepsie passport acceptance facility. Additional services and longer hours available.",
      icon: "ri-building-2-line",
    },
    {
      id: "hyde-park-3",
      name: "Rhinebeck Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "6387 Mill Street",
      city: "Rhinebeck",
      state: "NY",
      zip: "12572",
      phone: "845-876-4031",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Rhinebeck (~6 miles north)",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available. Convenient for Rhinebeck and northern Dutchess County residents.",
      icon: "ri-building-2-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Hyde Park?",
      a: "Fast Passport Center offers same-day emergency passport processing in as little as 24 hours for Hyde Park residents with documented life-or-death emergencies. For urgent travel within 2 weeks, our 3-5 day expedited service is the most popular option. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Where is the closest passport acceptance facility in Hyde Park?",
      a: "The Hyde Park Post Office at 424 Albany Post Road is the main Hyde Park passport acceptance facility. The Poughkeepsie Main Post Office at 55 Mansion Street and Rhinebeck Post Office at 6387 Mill Street are also convenient options. First-time applicants and those not eligible for mail-in renewal must apply in person.",
    },
    {
      q: "Can I get same-day passport photos in Hyde Park?",
      a: "Yes — passport photos are widely available at CVS Pharmacy, Walgreens, and Rite Aid locations in Hyde Park, Poughkeepsie, and Rhinebeck. We also offer photo review as part of our service to ensure your 2x2 photos meet all State Department requirements.",
    },
    {
      q: "How much does an expedited passport cost in Hyde Park?",
      a: "Government fees are set by the State Department and vary based on passport type (book vs card, large book option). Fast Passport Center's service fees depend on processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote based on your Hyde Park passport needs.",
    },
    {
      q: "Do I need to visit the New York Passport Agency in person?",
      a: "No — if you use Fast Passport Center, you do not need to visit the New York Passport Agency in Manhattan. As a registered courier, we handle the agency submission for you. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "What is the fastest way to renew a passport in Hyde Park?",
      a: "The fastest way to renew a passport in Hyde Park is through Fast Passport Center's 3-5 day expedited service. If your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged, you may qualify for renewal without an acceptance clerk visit. For even faster renewal, our 24-hour emergency service is available.",
    },
    {
      q: "Can I get a child passport expedited in Hyde Park?",
      a: "Yes — Fast Passport Center expedites child passports for minors under 16 in Hyde Park and all Dutchess County communities. Both parents must appear with the child or provide notarized consent (Form DS-3053). We review all parental consent documentation before submission to avoid delays.",
    },
    {
      q: "Does Fast Passport Center have an office in Hyde Park?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Hyde Park. We serve the Hudson Valley and all of Dutchess County through our secure overnight shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Hyde Park", address: "various CVS locations in Hyde Park, Poughkeepsie, and Rhinebeck", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Poughkeepsie", address: "various Walgreens locations in Poughkeepsie and Wappingers Falls", hours: "Mon-Sun, hours vary by location" },
    { name: "Rite Aid — Hyde Park", address: "Rite Aid locations in Hyde Park and nearby Dutchess County towns", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Poughkeepsie", address: "various UPS Store locations in Poughkeepsie", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Poughkeepsie", address: "various FedEx Office locations in Poughkeepsie", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
  ],
  dmvLocations: [
    {
      name: "NYS DMV — Poughkeepsie",
      address: "22 Mansion Street, Poughkeepsie, NY 12601",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Poughkeepsie DMV office for license renewals and REAL ID applications. Accepts walk-ins but appointments are strongly recommended.",
      featured: true,
    },
    {
      name: "NYS DMV — Newburgh",
      address: "2 Lake Street, Newburgh, NY 12550",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Newburgh DMV office serving western Dutchess County and Orange County residents.",
      featured: false,
    },
    {
      name: "NYS DMV — Wappingers Falls",
      address: "29 Marshall Road, Wappingers Falls, NY 12590",
      hours: "Mon-Fri 8:30 AM - 4:00 PM",
      note: "Wappingers Falls DMV office serving south Dutchess County and Hudson Valley corridor.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SWF",
      name: "Stewart International Airport",
      distance: "~30 miles from Hyde Park",
      address: "1180 First Street, New Windsor, NY 12553",
      note: "Stewart International is the closest major airport to Hyde Park with flights to major U.S. hubs including Orlando, Las Vegas, and seasonal European destinations. Perfect for Hudson Valley travelers.",
      flightUrl: "https://www.swfny.com/",
      flightLabel: "Stewart Airport",
      featured: true,
    },
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~70 miles from Hyde Park",
      address: "737 Albany Shaker Road, Albany, NY 12211",
      note: "Albany International is the major upstate airport with flights to major U.S. and international destinations. Alternative for Hyde Park area travelers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany Airport",
      featured: false,
    },
    {
      code: "JFK",
      name: "John F. Kennedy International Airport",
      distance: "~90 miles from Hyde Park",
      address: "JFK Airport, Queens, NY 11430",
      note: "Major international airport with flights to every continent. Alternative for Hyde Park area travelers needing international connections.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK Airport",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Hyde Park travelers with urgent departures within 24 hours to 2 weeks. Direct New York Passport Agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Hyde Park residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Hyde Park residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Hyde Park residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Hyde Park, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Hyde Park residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Hyde Park travelers with immediate departure needs from Stewart International or Albany." },
    { name: "Passport Renewal", description: "Fast passport renewal for Hyde Park residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Hyde Park residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Hyde Park residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Hyde Park and Dutchess County." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Hyde Park." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Hyde Park travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Hyde Park travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Hyde Park team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application directly to the New York Passport Agency.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Hyde Park address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "New York City", route: "/cities/new-york-city" },
    { name: "Brooklyn", route: "/cities/brooklyn" },
    { name: "Queens", route: "/cities/queens" },
    { name: "Buffalo", route: "/cities/buffalo" },
    { name: "Albany", route: "/cities/albany-ny" },
    { name: "Rochester", route: "/cities/rochester" },
    { name: "Syracuse", route: "/cities/syracuse" },
    { name: "Yonkers", route: "/cities/yonkers" },
    { name: "Utica", route: "/cities/utica" },
    { name: "Schenectady", route: "/cities/schenectady" },
    { name: "Troy", route: "/cities/troy" },
    { name: "Saratoga Springs", route: "/cities/saratoga-springs" },
    { name: "Elmira", route: "/cities/elmira" },
    { name: "Ithaca", route: "/cities/ithaca" },
    { name: "Corning", route: "/cities/corning" },
    { name: "Jamestown", route: "/cities/jamestown" },
    { name: "Plattsburgh", route: "/cities/plattsburgh" },
    { name: "Watertown", route: "/cities/watertown" },
    { name: "Ogdensburg", route: "/cities/ogdensburg" },
    { name: "Glens Falls", route: "/cities/glens-falls" },
    { name: "Kingston", route: "/cities/kingston" },
    { name: "Middletown", route: "/cities/middletown-ny" },
    { name: "New Paltz", route: "/cities/new-paltz" },
    { name: "Poughkeepsie", route: "/cities/poughkeepsie" },
    { name: "Newburgh", route: "/cities/newburgh" },
    { name: "Beacon", route: "/cities/beacon" },
    { name: "Fishkill", route: "/cities/fishkill" },
    { name: "Mount Vernon", route: "/cities/mount-vernon" },
    { name: "Tarrytown", route: "/cities/tarrytown" },
    { name: "Rhinebeck", route: "/cities/rhinebeck" },
    { name: "Red Hook", route: "/cities/red-hook" },
    { name: "New York State", route: "/states/new-york" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves Hyde Park and all of southern Dutchess County as a remote courier service. We do not have a physical office in Hyde Park, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — Hyde Park area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to Hyde Park and all of southern Dutchess County. From the historic FDR estate to the Hudson Valley, from Staatsburg to Rhinebeck — we serve the entire Hyde Park region with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve Hyde Park and all surrounding communities across southern Dutchess County and the Hudson Valley. Whether you're in Staatsburg, Rhinebeck, or Poughkeepsie, our expedited passport services are available to you.",
  howItWorksIntro:
    "Our Hyde Park customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes Hyde Park travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "Hyde Park travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about emergency and expedited passport services in Hyde Park. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/new-york",
  statePageName: "New York Passport Services",
};

export default hydeParkConfig;